import React, { createContext, useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import { AuthContext } from './AuthContext';
import { TradingContext } from './TradingContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const TradingHoursRestrictionContext = createContext(null);

/**
 * Single provider for trading hours restrictions and status.
 * Replaces per-component useTradingHoursRestriction to eliminate duplicate
 * trading_hours_restrictions and status requests on page load.
 */
export const TradingHoursRestrictionProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const { selectedAccount } = useContext(TradingContext);
  const token = user?.token || null;
  const accountId = selectedAccount?.AccountID || null;

  const [tradingHoursRestrictions, setTradingHoursRestrictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [canTrade, setCanTrade] = useState(true);
  const [lockoutReason, setLockoutReason] = useState(null);
  const [activeLockout, setActiveLockout] = useState(null);

  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const statusInFlightRef = useRef(null);
  const lastStatusAccountRef = useRef(null);

  const getHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${tokenRef.current}`
  }), []);

  const fetchTradingHoursRestrictions = useCallback(async () => {
    if (!tokenRef.current) return;

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/trading_hours_restrictions`, {
        method: 'GET',
        headers: getHeaders()
      });
      const json = await res.json();
      if (res.ok && json.success) {
        setTradingHoursRestrictions(json.restrictions || json.lockouts || []);
      } else {
        setError(json.error || 'Failed to fetch trading hours restrictions');
      }
    } catch (err) {
      console.error('[TradingHoursRestrictionContext] Error fetching restrictions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  const checkTradingStatus = useCallback(async (targetAccountId, { force = false } = {}) => {
    if (!tokenRef.current || !targetAccountId) {
      setCanTrade(true);
      setLockoutReason(null);
      setActiveLockout(null);
      lastStatusAccountRef.current = null;
      return;
    }

    // Collapse effect storms: one in-flight GET per account; skip repeats unless forced.
    if (statusInFlightRef.current === targetAccountId) return;
    if (!force && lastStatusAccountRef.current === targetAccountId) return;

    statusInFlightRef.current = targetAccountId;
    try {
      const res = await fetch(
        `${BACKEND_URL}/trading_hours_restrictions/status?accountId=${encodeURIComponent(targetAccountId)}`,
        {
          method: 'GET',
          headers: getHeaders()
        }
      );
      const json = await res.json();

      if (statusInFlightRef.current !== targetAccountId) return;

      if (res.ok && json.success) {
        setCanTrade(json.canTrade);
        setLockoutReason(json.reason);
        setActiveLockout(json.restriction || json.lockout);
        lastStatusAccountRef.current = targetAccountId;
      } else {
        setCanTrade(true);
        setLockoutReason(null);
        setActiveLockout(null);
        lastStatusAccountRef.current = targetAccountId;
      }
    } catch (err) {
      console.error('[TradingHoursRestrictionContext] Error checking trading status:', err);
      if (statusInFlightRef.current !== targetAccountId) return;
      setCanTrade(true);
      setLockoutReason(null);
      setActiveLockout(null);
    } finally {
      if (statusInFlightRef.current === targetAccountId) {
        statusInFlightRef.current = null;
      }
    }
  }, [getHeaders]);

  const createTradingHoursRestriction = useCallback(async (createAccountId, timeWindows, expiresAt) => {
    if (!tokenRef.current) return { success: false, error: 'Not authenticated' };

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/trading_hours_restrictions`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ accountId: createAccountId, timeWindows, expiresAt })
      });
      const json = await res.json();

      if (res.ok && json.success) {
        await fetchTradingHoursRestrictions();
        if (accountId === createAccountId) {
          await checkTradingStatus(createAccountId, { force: true });
        }
        return { success: true, restriction: json.restriction || json.lockout };
      } else {
        setError(json.error || 'Failed to create trading hours restriction');
        return { success: false, error: json.error, existingExpiresAt: json.existingExpiresAt };
      }
    } catch (err) {
      console.error('[TradingHoursRestrictionContext] Error creating restriction:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [getHeaders, fetchTradingHoursRestrictions, accountId, checkTradingStatus]);

  const deleteTradingHoursRestriction = useCallback(async (restrictionId, deleteAccountId) => {
    if (!tokenRef.current) return { success: false, error: 'Not authenticated' };

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/trading_hours_restrictions/${restrictionId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (res.status === 204) {
        await fetchTradingHoursRestrictions();
        if (accountId === deleteAccountId) {
          await checkTradingStatus(deleteAccountId, { force: true });
        }
        return { success: true };
      } else {
        const json = await res.json();
        setError(json.error || 'Failed to delete trading hours restriction');
        return { success: false, error: json.error, expiresAt: json.expiresAt };
      }
    } catch (err) {
      console.error('[TradingHoursRestrictionContext] Error deleting restriction:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [getHeaders, fetchTradingHoursRestrictions, accountId, checkTradingStatus]);

  const getRestriction = useCallback((id) => {
    return tradingHoursRestrictions.find(r => r.account_id === id);
  }, [tradingHoursRestrictions]);

  const hasActiveRestriction = useCallback((id) => {
    const restriction = getRestriction(id);
    if (!restriction) return false;
    return new Date(restriction.expires_at) > new Date();
  }, [getRestriction]);

  const lockoutMessage = useMemo(() => {
    if (canTrade || !activeLockout) return null;
    return lockoutReason || 'Trading disabled: Outside allowed trading hours';
  }, [canTrade, activeLockout, lockoutReason]);

  // Status check: primitive deps only (accountId + token presence).
  useEffect(() => {
    if (!accountId || !token) {
      lastStatusAccountRef.current = null;
      setCanTrade(true);
      setLockoutReason(null);
      setActiveLockout(null);
      return undefined;
    }
    void checkTradingStatus(accountId, { force: false });
    return undefined;
  }, [accountId, token, checkTradingStatus]);

  useEffect(() => {
    if (!token) return undefined;
    void fetchTradingHoursRestrictions();
    return undefined;
  }, [token, fetchTradingHoursRestrictions]);

  useEffect(() => {
    if (!accountId || !token) return undefined;

    const interval = setInterval(() => {
      void checkTradingStatus(accountId, { force: true });
    }, 30000);

    return () => clearInterval(interval);
  }, [accountId, token, checkTradingStatus]);

  const value = useMemo(() => ({
    tradingHoursRestrictions,
    loading,
    error,
    canTrade,
    lockoutMessage,
    activeLockout,
    fetchTradingHoursRestrictions,
    createTradingHoursRestriction,
    deleteTradingHoursRestriction,
    checkTradingStatus,
    getRestriction,
    hasActiveRestriction,
    sessionLockouts: tradingHoursRestrictions,
    fetchSessionLockouts: fetchTradingHoursRestrictions,
    createSessionLockout: createTradingHoursRestriction,
    deleteSessionLockout: deleteTradingHoursRestriction,
    getLockout: getRestriction,
    hasActiveLockout: hasActiveRestriction
  }), [
    tradingHoursRestrictions,
    loading,
    error,
    canTrade,
    lockoutMessage,
    activeLockout,
    fetchTradingHoursRestrictions,
    createTradingHoursRestriction,
    deleteTradingHoursRestriction,
    checkTradingStatus,
    getRestriction,
    hasActiveRestriction
  ]);

  return (
    <TradingHoursRestrictionContext.Provider value={value}>
      {children}
    </TradingHoursRestrictionContext.Provider>
  );
};
